import React, { useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Package, Truck, Ruler } from 'lucide-react';
import FormField from './components/FormField';
import SwitchField from './components/SwitchField';
import useOptimizedFormWatch from './hooks/useOptimizedFormWatch';

export default function PhysicalFields() {
  const form = useFormContext();
  const watchedValues = useOptimizedFormWatch();

  // Memoize handlers to prevent unnecessary re-renders
  const handleShippingAddressChange = useCallback(
    (checked: boolean) => {
      form.setValue('requires_shipping_address', checked);
    },
    [form],
  );

  // Memoize conditional sections to prevent unnecessary re-renders

  const dimensionsSection = useMemo(() => {
    return (
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
          <Ruler className="w-4 h-4 mr-2" />
          Physical Dimensions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Weight (kg)" color="green">
            <Input
              type="number"
              step="0.001"
              min="0"
              placeholder="0.254"
              className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-lg transition-all duration-200 text-sm"
              {...form.register('weight', { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Length (cm)" color="green">
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="20.0"
              className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-lg transition-all duration-200 text-sm"
              {...form.register('length', { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Width (cm)" color="green">
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="17.0"
              className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-lg transition-all duration-200 text-sm"
              {...form.register('width', { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Height (cm)" color="green">
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="7.0"
              className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-lg transition-all duration-200 text-sm"
              {...form.register('height', { valueAsNumber: true })}
            />
          </FormField>
        </div>
      </div>
    );
  }, [form]);

  const shippingSettingsSection = useMemo(() => {
    return (
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
          <Truck className="w-4 h-4 mr-2" />
          Shipping Configuration
        </h3>
        <div className="space-y-3">
          <SwitchField
            label="Requires Shipping Address"
            description="Customer must provide shipping address"
            checked={watchedValues.requiresShippingAddress}
            onChange={handleShippingAddressChange}
          />
        </div>
      </div>
    );
  }, [
    form,
    watchedValues.requiresShippingAddress,
    handleShippingAddressChange,
  ]);

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex shrink-0 items-center justify-center">
            <Package className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Physical Product Configuration
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Configure shipping and physical product settings
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Physical Dimensions */}
        {dimensionsSection}

        {/* Shipping Configuration */}
        {shippingSettingsSection}
      </div>
    </div>
  );
}
